/**
 * useAgentDrafts — the pending Inbox-Agent replies shown in the inbox banner.
 *
 * When an incoming mail matches one of the user's agent cases, the backend
 * (functions/EmailAgentProcessor.js) pre-writes an answer into RTDB under
 * `/World/{uid}/emailDrafts` and stamps `hasDraft` / `draftId` /
 * `agentAction: 'draft_created'` onto the mail document.
 *
 * This hook returns only the drafts that are still worth acting on: unsent,
 * recent, and belonging to a mail that still exists and hasn't been answered
 * elsewhere. The classic banner skipped those checks, so a draft for a deleted
 * or already-answered mail stayed in the list indefinitely.
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { ref, query, orderByChild, limitToLast, onValue, remove } from 'firebase/database';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { database } from '../../../firebaseConfig';

// The backend only ever queues a handful at a time; more than this in the
// banner would be a queue, not a nudge.
const MAX_DRAFTS = 20;
// A reply that has been sitting unsent for a week is no longer timely — the
// conversation has moved on, and sending it now does more harm than good.
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

// Drafts carry a numeric RTDB server timestamp plus an ISO `createdAt`; older
// ones may only have the latter.
const draftTime = (draft) => {
  if (typeof draft?.timestamp === 'number') return draft.timestamp;
  const parsed = Date.parse(draft?.createdAt || '');
  return Number.isNaN(parsed) ? 0 : parsed;
};

export default function useAgentDrafts(userId) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  // draftId -> true for drafts whose original mail is gone, deleted or already
  // answered. Kept separate from `candidates` so a fresh RTDB snapshot doesn't
  // resurrect a card we already ruled out.
  const [stale, setStale] = useState({});
  // draftId -> bool, so each draft is validated against Firestore only once.
  const checkedRef = useRef(new Map());

  useEffect(() => {
    checkedRef.current = new Map();
    setStale({});
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      setCandidates([]);
      setLoading(false);
      return undefined;
    }

    const draftsQuery = query(
      ref(database, `/World/${userId}/emailDrafts`),
      orderByChild('timestamp'),
      limitToLast(MAX_DRAFTS),
    );

    const unsub = onValue(
      draftsQuery,
      (snap) => {
        const now = Date.now();
        const out = [];
        snap.forEach((child) => {
          const draft = { ...child.val(), id: child.key };
          if (draft.status !== 'not sent') return;
          if (!draft.draftContent || !draft.originalEmailId) return;
          const ts = draftTime(draft);
          if (ts && now - ts > MAX_AGE_MS) return;
          out.push({ ...draft, ts });
        });
        out.sort((a, b) => b.ts - a.ts);
        setCandidates(out);
        setLoading(false);
      },
      (err) => {
        console.warn('[InboxView] agent drafts listener failed:', err?.message);
        setCandidates([]);
        setLoading(false);
      },
    );

    return () => unsub();
  }, [userId]);

  // Validate each new draft against its original mail. Runs once per draft and
  // never blocks rendering — a card simply disappears if it turns out stale.
  useEffect(() => {
    if (!userId || candidates.length === 0) return undefined;
    const pending = candidates.filter((d) => !checkedRef.current.has(d.id));
    if (pending.length === 0) return undefined;

    let cancelled = false;
    const db = getFirestore();

    (async () => {
      const dead = [];
      for (const draft of pending) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const snap = await getDoc(doc(db, 'world_inbox', userId, 'emails', draft.originalEmailId));
          const mail = snap.exists() ? snap.data() : null;
          const alive = !!mail && mail.deleted !== true && mail.isReplied !== true;
          checkedRef.current.set(draft.id, alive);
          if (!alive) dead.push(draft.id);
        } catch (err) {
          // An unreadable mail is treated as alive: showing a card we can't
          // verify is far less harmful than silently dropping a real reply.
          checkedRef.current.set(draft.id, true);
        }
      }
      if (!cancelled && dead.length > 0) {
        setStale((prev) => {
          const next = { ...prev };
          dead.forEach((id) => { next[id] = true; });
          return next;
        });
      }
    })();

    return () => { cancelled = true; };
  }, [userId, candidates]);

  const drafts = useMemo(
    () => candidates.filter((d) => !stale[d.id]),
    [candidates, stale],
  );

  /**
   * Drops a draft the user doesn't want. Hides it immediately, then removes it
   * from RTDB so it can't come back on the next snapshot.
   */
  const dismissDraft = useCallback(async (draftId) => {
    if (!userId || !draftId) return;
    checkedRef.current.set(draftId, false);
    setStale((prev) => ({ ...prev, [draftId]: true }));
    try {
      await remove(ref(database, `/World/${userId}/emailDrafts/${draftId}`));
    } catch (err) {
      console.warn('[InboxView] agent draft dismiss failed:', err?.message);
    }
  }, [userId]);

  return { drafts, loading, dismissDraft };
}
